import { useContext, useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Column } from 'react-table'
import Table from '../../../global/components/table/Table'
import BasicFilter from '../../../global/components/table/helpers/BasicFilter'
import WorkOrderDetailModal from '../work-order-detail/components/main/WorkOrderDetailModal'
import { WoDetailContext } from '../context/context'
import { handleColumnFilterUpdate } from '../../../global/components/table/helpers/multiColumnFilters'
import createSearchParamsString from '../../../global/utils/createSearchParamsString'
import { IOrder } from '../../../../../app/entities/Order'
import { ILocationFilters } from '../main/WorkOrdersMain'
import removeDoubleCommas from '../../../global/utils/removeDoubleCommas'
import { ITechnician } from '../../../../../app/entities/Technician'
import { DELIVERY_STATUSES_MAP } from '../../../global/constants/order-status'
import isDeliveryByOrderService from '../../../global/utils/is-delivery-by-order-service'
import { SERVICE_NAMES } from '../../../global/constants/service'
import { getAllTireRackOrderNumbers } from '../../../global/utils/tire-rack-orders'
import fetchDataAndSetTable from '../../../global/components/table/helpers/fetchDataAndSetTable'
import LockIcon from '@mui/icons-material/Lock'

interface IOrderWithTechnician extends IOrder {
  technician?: Pick<ITechnician, 'objectId' | 'name'> | null
}

interface IWorkOrdersTableProps {
  locationFilters: ILocationFilters
}
function WorkOrdersTable(props: IWorkOrdersTableProps) {
  const { locationFilters } = props
  const { id } = useParams()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const [tableState, setTableState] = useState({
    data: [] as IOrderWithTechnician[],
    isLoading: false,
    count: 0,
    pageSize: 25,
    offSet: 0,
  })
  const [columnFilters, setColumnFilters] = useState({})
  const { queryOrderData, clearData } = useContext(WoDetailContext)

  async function handleOpen(id: string) {
    setIsModalOpen(true)
    await queryOrderData(id)

    const queryStringParams = await createSearchParamsString(searchParams)
    const url = `/work-orders/${id}` + queryStringParams
    navigate(url)
  }

  function handleClosed() {
    setIsModalOpen(false)
    navigate('/work-orders')
    clearData()
    retrieveTableData()
  }
  function onRecordClickHandler(row: any) {
    const {
      original: { objectId },
    } = row
    handleOpen(objectId)
  }

  function onColumnFilterUpdate(filtersUpdate: object) {
    handleColumnFilterUpdate(filtersUpdate, columnFilters, setColumnFilters)
  }

  useEffect(() => {
    if (id && !isModalOpen) {
      handleOpen(id)
    }
  }, [id])

  const getAllOrdersTableEndpoint = `${process.env.REACT_APP_ORIGIN}/orders/getAllOrders`
  function retrieveTableData() {
    fetchDataAndSetTable<IOrderWithTechnician>({
      endpoint: getAllOrdersTableEndpoint,
      queries: [
        { columnFilters: JSON.stringify(columnFilters) },
        { selectedLocationId: locationFilters.locationFilter },
        { pageSize: tableState.pageSize.toString() },
        { offSet: tableState.offSet.toString() },
        { sortBy: '`workOrderId` DESC' },
      ],
      setTableState,
    })
  }

  useEffect(() => {
    retrieveTableData()
  }, [locationFilters, tableState.pageSize, tableState.offSet])

  const columns: Column<IOrderWithTechnician | any>[] = [
    {
      Header: 'Order ID',
      accessor: 'workOrderId',
      Filter: BasicFilter({
        onChange: (val: string) => onColumnFilterUpdate({ workOrderId: val }),
        onEnter: retrieveTableData,
      }),
    },
    {
      Header: 'TR Order ID',
      accessor: 'tireRackOrderNumbers',
      Cell: ({ row: { original } }) => {
        const allTireRackOrderNumbers = getAllTireRackOrderNumbers(original)
        if (allTireRackOrderNumbers.length > 0) {
          return <div>{allTireRackOrderNumbers.join(', ')}</div>
        } else {
          return <div></div>
        }
      },
      Filter: BasicFilter({
        onChange: (val: string) =>
          onColumnFilterUpdate({
            tireRackOrders: {
              orderNumber: val,
            },
          }),
        onEnter: retrieveTableData,
      }),
    },
    {
      Header: 'Location',
      accessor: 'location.name',
      Filter: BasicFilter({
        onChange: (val: string) =>
          onColumnFilterUpdate({ location: { name: val } }),
        onEnter: retrieveTableData,
      }),
    },
    {
      Header: 'Technician Name',
      Cell: ({ row: { original } }) => {
        const { routeInstructions, technician, lockedTechnician } =
          original as IOrderWithTechnician

        let technicianName: string = ''
        let isLocked = false

        const routeInstruction = routeInstructions?.[0]

        if (lockedTechnician?.name) {
          isLocked = true
        }
        if (routeInstruction?.route?.technician?.name) {
          // if already routed
          technicianName = routeInstruction?.route?.technician?.name
        } else if (technician?.name) {
          // if only planned
          technicianName = technician?.name
        }

        return (
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 4,
            }}
          >
            {isLocked && (
              <div>
                <LockIcon fontSize='small' />
              </div>
            )}
            {technicianName}
          </div>
        )
      },
      Filter: BasicFilter({
        onChange: (val: string) =>
          onColumnFilterUpdate({
            routeArea: {
              technician: {
                name: val,
              },
            },
          }),
        onEnter: retrieveTableData,
      }),
    },
    {
      Header: 'Status',
      accessor: 'status',
      Cell: ({ row: { original } }) => {
        const { status, orderServices } = original as IOrderWithTechnician
        const isDelivery = isDeliveryByOrderService(orderServices || [])
        if (isDelivery) {
          return <div>{DELIVERY_STATUSES_MAP[status || ''] || status}</div>
        } else {
          return <div>{status}</div>
        }
      },
      Filter: BasicFilter({
        onChange: (val: string) => {
          const deliveryStatuses = Object.values(DELIVERY_STATUSES_MAP)
          if (deliveryStatuses.includes(val)) {
            const mappedStatus = Object.keys(DELIVERY_STATUSES_MAP).find(
              (key) => DELIVERY_STATUSES_MAP[key] === val,
            )
            onColumnFilterUpdate({
              status: mappedStatus || val,
              orderServices: {
                service: { name: SERVICE_NAMES.TIRE_DELIVERY },
              },
            })
          } else {
            onColumnFilterUpdate({
              status: val,
              orderServices: {
                service: '', //we have to remove the delivery only query above but we also have no way of querying non delivery orders
              },
            })
          }
        },
        onEnter: retrieveTableData,
      }),
    },
    {
      Header: 'Service Appt',
      accessor: 'timeSlot.fromDateGenerated',
      Filter: BasicFilter({
        onChange: (val: string) =>
          onColumnFilterUpdate({
            timeSlot: {
              fromDateGenerated: val,
            },
          }),
        onEnter: retrieveTableData,
      }),
      sortInverted: true,
    },
    {
      Header: 'Time Slot',
      accessor: 'timeSlot.type',
      Filter: BasicFilter({
        onChange: (val: string) =>
          onColumnFilterUpdate({
            timeSlot: {
              type: val,
            },
          }),
        onEnter: retrieveTableData,
      }),
    },
    {
      Header: 'First Name',
      accessor: 'customer.firstName',
      Filter: BasicFilter({
        onChange: (val: string) =>
          onColumnFilterUpdate({ customer: { firstName: val } }),
        onEnter: retrieveTableData,
      }),
    },
    {
      Header: 'Last Name',
      accessor: 'customer.lastName',
      Filter: BasicFilter({
        onChange: (val: string) =>
          onColumnFilterUpdate({ customer: { lastName: val } }),
        onEnter: retrieveTableData,
      }),
    },
    {
      Header: 'Customer Email',
      accessor: 'customer.email',
      Filter: BasicFilter({
        onChange: (val: string) =>
          onColumnFilterUpdate({ customer: { email: val } }),
        onEnter: retrieveTableData,
      }),
    },
    {
      Header: 'Customer Phone',
      accessor: 'customer.trimmedPhoneNumber',
      Filter: BasicFilter({
        onChange: (val: string) =>
          onColumnFilterUpdate({ customer: { trimmedPhoneNumber: val } }),
        onEnter: retrieveTableData,
      }),
    },
    {
      Header: 'Installation Address',
      accessor: 'installationPoint.fullAddress',
      Cell: ({ row: { original } }) => {
        const { installationPoint } = original as IOrderWithTechnician
        if (installationPoint) {
          return <div>{removeDoubleCommas(installationPoint.fullAddress)}</div>
        } else {
          return <div></div>
        }
      },
      Filter: BasicFilter({
        onChange: (val: string) =>
          onColumnFilterUpdate({ installationPoint: { fullAddress: val } }),
        onEnter: retrieveTableData,
      }),
    },
  ]

  return (
    <div style={{ width: '100%' }}>
      <WorkOrderDetailModal
        handleClosed={handleClosed}
        isOpen={isModalOpen}
      />
      <Table
        columns={columns}
        data={tableState.data ?? []}
        isLoading={tableState.isLoading}
        onRowClick={onRecordClickHandler}
        isPaginated
        pagination={{ setTableState, tableState }}
        isTableTopperPresent={false}
      />
    </div>
  )
}

export default WorkOrdersTable
